import { useEffect, useRef, useSyncExternalStore } from "react";

import {
  getWorkspaceRefreshScopeStatus,
  registerWorkspaceRefreshScope,
  subscribeWorkspaceRefreshScopes,
  type RegisterWorkspaceRefreshScopeInput,
  type WorkspaceRefreshScopeStatus,
} from "./workspace-refresh-service.js";

export type UseWorkspaceRefreshScopeInput<TPayload> =
  RegisterWorkspaceRefreshScopeInput<TPayload> & {
    enabled?: boolean;
  };

function createSupersededRefreshError(): Error {
  const error = new Error("workspace-refresh-registration-superseded");
  error.name = "AbortError";
  return error;
}

export function useWorkspaceRefreshScope<TPayload>(
  input: UseWorkspaceRefreshScopeInput<TPayload>,
): WorkspaceRefreshScopeStatus {
  const inputRef = useRef(input);
  inputRef.current = input;

  const status = useSyncExternalStore(
    subscribeWorkspaceRefreshScopes,
    () => getWorkspaceRefreshScopeStatus(input.key),
    () => getWorkspaceRefreshScopeStatus(input.key),
  );

  useEffect(() => {
    if (input.enabled === false) {
      return () => {};
    }

    const registeredKey = input.key;
    const readCurrentInput = (): UseWorkspaceRefreshScopeInput<TPayload> | null =>
      inputRef.current.key === registeredKey ? inputRef.current : null;

    return registerWorkspaceRefreshScope<TPayload>({
      key: input.key,
      aliases: input.aliases,
      minStaleMs: input.minStaleMs,
      activePollMs: input.activePollMs,
      debounceMs: input.debounceMs,
      refreshOnRegister: input.refreshOnRegister,
      loader: (signal) => {
        const currentInput = readCurrentInput();

        return currentInput
          ? currentInput.loader(signal)
          : Promise.reject(createSupersededRefreshError());
      },
      apply: (payload) => readCurrentInput()?.apply(payload),
      onError: (error) => readCurrentInput()?.onError?.(error),
      readGeneratedAt: (payload) =>
        readCurrentInput()?.readGeneratedAt?.(payload) ?? null,
      readRevision: (payload) =>
        readCurrentInput()?.readRevision?.(payload) ?? null,
    });
  }, [
    input.activePollMs,
    input.debounceMs,
    input.enabled,
    input.key,
    input.minStaleMs,
    input.refreshOnRegister,
  ]);

  return status;
}
